import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import {
  Email as EmailIcon,
  EmailOutlined as EmailOutIcon,
  Description as DokumentIcon,
  Assignment as TicketIcon,
  EventNote as FristIcon,
  OpenInNew as OpenIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import { adressenApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface HistorieEintrag {
  typ: string;
  id: number;
  titel: string;
  richtung: string | null;
  datum: string;
  beschreibung: string | null;
}

interface KundenHistorieProps {
  adresseId: number;
  compact?: boolean;
}

const KundenHistorie: React.FC<KundenHistorieProps> = ({ adresseId, compact = false }) => {
  const navigate = useNavigate();
  const [historie, setHistorie] = useState<HistorieEintrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadHistorie();
  }, [adresseId]);

  const loadHistorie = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adressenApi.getHistorie(adresseId, 100);
      setHistorie(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Historie:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Historie');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (eintrag: HistorieEintrag) => {
    switch (eintrag.typ) {
      case 'Email':
        return eintrag.richtung === 'Eingehend' ? (
          <EmailIcon color="info" />
        ) : (
          <EmailOutIcon color="primary" />
        );
      case 'Dokument':
        return <DokumentIcon color="success" />;
      case 'Ticket':
        return <TicketIcon color="warning" />;
      case 'Frist':
        return <FristIcon color="error" />;
      default:
        return <TimelineIcon />;
    }
  };

  const getChipColor = (eintrag: HistorieEintrag) => {
    switch (eintrag.typ) {
      case 'Email':
        return eintrag.richtung === 'Eingehend' ? 'info' : 'primary';
      case 'Dokument':
        return 'success';
      case 'Ticket':
        return 'warning';
      case 'Frist':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleOpen = (eintrag: HistorieEintrag) => {
    switch (eintrag.typ) {
      case 'Email':
        navigate(`/emails?id=${eintrag.id}`);
        break;
      case 'Dokument':
        navigate(`/dokumente/${eintrag.id}`);
        break;
      case 'Ticket':
        navigate(`/tickets?id=${eintrag.id}`);
        break;
      case 'Frist':
        navigate(`/fristen?id=${eintrag.id}`);
        break;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Heute, ${date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Gestern, ${date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `Vor ${diffDays} Tagen`;
    } else {
      return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };

  const displayedHistorie = compact && !showAll ? historie.slice(0, 5) : historie;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (historie.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <TimelineIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography color="textSecondary">Keine Aktivitäten vorhanden</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: compact ? 1 : 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <TimelineIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            Aktivitäten
          </Typography>
          <Chip label={historie.length} size="small" />
        </Box>
        <Tooltip title="Aktualisieren">
          <IconButton size="small" onClick={loadHistorie}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <List dense disablePadding>
        {displayedHistorie.map((eintrag, index) => (
          <React.Fragment key={`${eintrag.typ}-${eintrag.id}`}>
            {index > 0 && <Divider component="li" />}
            <ListItem
              sx={{
                py: 1,
                px: compact ? 0 : 1,
                '&:hover': { bgcolor: 'action.hover' },
                cursor: 'pointer',
              }}
              onClick={() => handleOpen(eintrag)}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{getIcon(eintrag)}</ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: compact ? 200 : 300 }}>
                      {eintrag.titel || '(Ohne Titel)'}
                    </Typography>
                    <Chip
                      label={eintrag.typ}
                      size="small"
                      color={getChipColor(eintrag) as any}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    {eintrag.richtung && eintrag.typ === 'Email' && (
                      <Tooltip title={eintrag.richtung}>
                        {eintrag.richtung === 'Eingehend' ? (
                          <ArrowDownIcon fontSize="small" color="info" />
                        ) : (
                          <ArrowUpIcon fontSize="small" color="primary" />
                        )}
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                      {eintrag.beschreibung || formatDate(eintrag.datum)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatDate(eintrag.datum)}
                    </Typography>
                  </Box>
                }
              />
              <Tooltip title="Öffnen">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpen(eintrag); }}>
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {compact && historie.length > 5 && (
        <Box textAlign="center" mt={1}>
          <Button
            size="small"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Weniger anzeigen' : `Alle ${historie.length} anzeigen`}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default KundenHistorie;
